import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import type { ColDef, ValueGetterParams } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Car, Bike, Truck, Bus } from 'lucide-react';

import {
	ActiveInactiveCellRenderer,
	DeleteCellRenderer
} from '@/components/grid';
import { ListPageLayout } from '@/components/layout/list-page-layout';
import { openEntityInNewWindow } from '@/lib/open-in-new-window';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { StatusBadge } from '@/components/ui/status-badge';
import { useResourceNavigation, useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { getVehicleMakeLogoUrl } from '@/hooks/useVehicleMakesModels';

import { cloneVehicle } from './cloneVehicle';
import { showMutationSuccessToast } from '@/lib/mutationRefetch';

import { VehicleDetails } from './components/vehicle-details';
import { selectVehicleAfterSave } from './vehicle-list-selection';
import { mapVehicleToFormData } from './vehicle-input';
import { VEHICLES_LIST_QUERY, VEHICLE_QUERY, VEHICLE_DELETE_MUTATION } from './vehicles.gql';

interface VehicleTypeSummary {
	name?: string | null;
}

interface VehicleCustomerSummary {
	displayName?: string | null;
}

interface VehicleListRecord {
	vehicleId: string;
	plateNumber?: string | null;
	vehicleTypeId?: number | null;
	customerId?: string | null;
	vehicleType?: VehicleTypeSummary | null;
	customer?: VehicleCustomerSummary | null;
	make?: string | null;
	model?: string | null;
	isActive: boolean;
}

interface VehiclesListResponse {
	vehicles?: {
		nodes?: VehicleListRecord[] | null;
		pageInfo?: {
			hasNextPage: boolean;
			endCursor?: string | null;
		} | null;
	} | null;
}

const resolveMakeModel = (vehicle: VehicleListRecord) => {
	const make = vehicle.make?.trim();
	const model = vehicle.model?.trim();

	if (make && model) {
		return `${make} ${model}`;
	}

	if (make) {
		return make;
	}

	if (model) {
		return model;
	}

	return '—';
};

const resolveCustomerName = (vehicle: VehicleListRecord) => {

	const displayName = vehicle.customer?.displayName?.trim();
	return displayName || null;
};

const getStatusBadge = (vehicle: VehicleListRecord) => {
	return <StatusBadge isActive={vehicle.isActive} />;
};

const getVehicleIcon = (vehicleTypeName?: string | null) => {
	const name = vehicleTypeName?.toLowerCase() ?? '';

	if (name.includes('motorcycle') || name.includes('bike') || name.includes('scooter')) {
		return Bike;
	}
	if (name.includes('truck') || name.includes('lorry') || name.includes('van')) {
		return Truck;
	}
	if (name.includes('bus') || name.includes('coach')) {
		return Bus;
	}
	return Car;
};

const VehicleEntityIcon = ({
	vehicleTypeName,
	makeName
}: {
	vehicleTypeName?: string | null;
	makeName?: string | null;
}) => {
	const IconComponent = getVehicleIcon(vehicleTypeName);
	const logoUrl = getVehicleMakeLogoUrl(makeName);
	const [logoError, setLogoError] = useState(false);

	useEffect(() => {
		setLogoError(false);
	}, [logoUrl]);

	// Show logo if available and not errored
	if (logoUrl && !logoError) {
		return (
			<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent flex-shrink-0 overflow-hidden">
				<img
					src={logoUrl}
					alt={`${makeName} logo`}
					className="w-full h-full object-cover"
					onError={() => setLogoError(true)}
				/>
			</div>
		);
	}

	// Fallback to vehicle type icon
	return (
		<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
			<IconComponent className="w-7 h-7 text-gray-600 dark:text-gray-300" />
		</div>
	);
};

export const VehiclesList = () => {
	const { t } = useTranslation('vehicles');
	const apolloClient = useApolloClient();
	const location = useLocation();
	const navigate = useNavigate();
	const [selectedVehicle, setSelectedVehicle] =
		useState<VehicleListRecord | null>(null);
	const [activeDetailsTab, setActiveDetailsTab] = useState('details');
	const { goToCreate, goToEdit } = useResourceNavigation('vehicles');
	const go = useGo();
	const { showDialog } = useConfirmationDialog();

	// Track total items to fetch (for preserving count after create/edit refetch)
	const [itemsToFetch, setItemsToFetch] = useState(DEFAULT_PAGE_SIZE);

	const { data, loading, error, refetch, fetchMore } =
		useQuery<VehiclesListResponse>(VEHICLES_LIST_QUERY, {
			variables: {
				first: itemsToFetch
			}
		});

	// Pagination state
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const hasNextPage = data?.vehicles?.pageInfo?.hasNextPage ?? false;
	const endCursor = data?.vehicles?.pageInfo?.endCursor;

	// Load more handler using fetchMore
	const handleLoadMore = useCallback(async () => {
		if (!hasNextPage || isLoadingMore || !endCursor) return;

		setIsLoadingMore(true);
		try {
			await fetchMore({
				variables: {
					first: DEFAULT_PAGE_SIZE,
					after: endCursor
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.vehicles) return prev;

					return {
						...prev,
						vehicles: {
							...fetchMoreResult.vehicles,
							nodes: [
								...(prev.vehicles?.nodes ?? []),
								...(fetchMoreResult.vehicles.nodes ?? [])
							]
						}
					};
				}
			});
			// Reset local vehicles to use server data after fetchMore
			setLocalVehicles([]);
		} catch (error) {
			console.error('Error loading more vehicles:', error);
		} finally {
			setIsLoadingMore(false);
		}
	}, [fetchMore, hasNextPage, isLoadingMore, endCursor]);

	const handleRefetch = () => {
		setLocalVehicles([]);
		setItemsToFetch(DEFAULT_PAGE_SIZE);
		refetch({ first: DEFAULT_PAGE_SIZE });
	};

	const [deleteVehicle, { loading: deleteLoading }] = useMutation(
		VEHICLE_DELETE_MUTATION
	);

	// Local state to manage vehicle list data with imperative updates
	const [localVehicles, setLocalVehicles] = useState<VehicleListRecord[]>([]);

	const vehicles = useMemo(() => {
		const serverVehicles = data?.vehicles?.nodes ?? [];
		// Always prefer server data - local state is only for optimistic updates
		if (localVehicles.length === 0 || serverVehicles.length === 0) {
			return serverVehicles;
		}
		// Use local state only when both have data (for optimistic updates)
		return localVehicles;
	}, [data?.vehicles?.nodes, localVehicles]);

	// Update local vehicles when query data changes (but only if no local override exists)
	useEffect(() => {
		if (!loading && data?.vehicles?.nodes && localVehicles.length === 0) {
			setLocalVehicles(data.vehicles.nodes);
		}
	}, [data?.vehicles?.nodes, loading, localVehicles.length]);

	// Handle vehicle selection from location state (after create/edit)
	useEffect(() => {
		const state = location.state as { selectVehicleId?: string } | null;
		if (state?.selectVehicleId && vehicles.length > 0 && !loading) {
			const vehicleIdToSelect = state.selectVehicleId;
			// Trigger the selection after a short delay to ensure grid is ready
			const timeoutId = setTimeout(async () => {
				try {
					await selectVehicleAfterSave({
						vehicleId: vehicleIdToSelect,
						client: apolloClient,
						currentVehicles: vehicles,
						onDataUpdate: setLocalVehicles,
						onVehicleSelect: setSelectedVehicle
					});
					// Clear the location state after processing to prevent re-triggering
					navigate(location.pathname, { replace: true, state: null });
				} catch (error) {
					console.error(
						'Failed to select vehicle after navigation:',
						error
					);
				}
			}, 100);

			return () => clearTimeout(timeoutId);
		}
	}, [
		location.state,
		vehicles.length,
		loading,
		apolloClient,
		vehicles,
		navigate,
		location.pathname
	]);

	// Helper function to find the next vehicle to select after deletion
	const findNextVehicleToSelect = (
		deletedVehicle: VehicleListRecord,
		vehicleList: VehicleListRecord[]
	) => {
		const currentIndex = vehicleList.findIndex(
			v => v.vehicleId === deletedVehicle.vehicleId
		);

		if (vehicleList.length <= 1) {
			return null;
		}

		// Try to select the next vehicle (same index after deletion)
		if (currentIndex < vehicleList.length - 1) {
			return vehicleList[currentIndex + 1];
		}

		// If deleting the last vehicle, select the previous one
		if (currentIndex > 0) {
			return vehicleList[currentIndex - 1];
		}

		return null;
	};

	// Helper function to handle post-deletion selection
	const handlePostDeletionSelection = (
		nextVehicleToSelect: VehicleListRecord | null,
		updatedVehicles: VehicleListRecord[]
	) => {
		// First, clear the current selection completely
		setSelectedVehicle(null);

		// Then after a brief delay, set the next vehicle
		setTimeout(() => {
			if (nextVehicleToSelect && updatedVehicles.length > 0) {
				// Find the vehicle in the updated list by ID
				const updatedVehicle = updatedVehicles.find(
					v => v.vehicleId === nextVehicleToSelect?.vehicleId
				);

				if (updatedVehicle) {
					setSelectedVehicle(updatedVehicle);
				}
			}
		}, 100);
	};

	const handleDeleteVehicle = async (vehicle: VehicleListRecord) => {
		const plateDisplay = vehicle.plateNumber?.trim() || 'Unknown plate';
		const confirmed = await showDialog(
			'Delete Vehicle',
			`Are you sure you want to delete vehicle "${plateDisplay}"? This action cannot be undone.`
		);

		if (confirmed) {
			// Determine if we need to find the next vehicle to select
			const wasSelectedVehicle =
				selectedVehicle?.vehicleId === vehicle.vehicleId;

			// Clear selection immediately if deleting the selected vehicle
			if (wasSelectedVehicle) {
				setSelectedVehicle(null);
			}

			try {
				const result = await deleteVehicle({
					variables: {
						input: {
							vehicleId: vehicle.vehicleId
						}
					},
					refetchQueries: ['Vehicles']
				});

				const checkResult = result.data?.deleteVehicle;

				if (checkResult?.success) {
					showMutationSuccessToast('vehicles', 'delete');

					// Find the next vehicle to select after deletion (before refetch)
					let nextVehicleToSelect = null;
					if (wasSelectedVehicle) {
						nextVehicleToSelect = findNextVehicleToSelect(
							vehicle,
							vehicles
						);
					}

					// Refetch data and update local state with new data (don't clear first to preserve animations)
					refetch().then(result => {
						const updatedVehicles =
							result.data?.vehicles?.nodes ?? [];
						// Update local state with new data directly
						setLocalVehicles(updatedVehicles);
						handlePostDeletionSelection(
							nextVehicleToSelect,
							updatedVehicles
						);
					});
				} else {
					const errorMessage = extractCheckResultError(checkResult);
					toast.error(`Failed to delete vehicle: ${errorMessage}`);
				}
			} catch (error) {
				console.error('Delete vehicle error:', error);
				toast.error('Failed to delete vehicle. Please try again.');
			}
		}
	};

	const handleDeleteVehicleById = async (vehicleId: string) => {
		const vehicle = vehicles.find(v => v.vehicleId === vehicleId);
		if (vehicle) {
			await handleDeleteVehicle(vehicle);
		}
	};

	const handleCloneVehicle = async (vehicleId: string) => {
		try {
			// Fetch full vehicle details for cloning
			const { data } = await apolloClient.query({
				query: VEHICLE_QUERY,
				variables: { id: vehicleId },
				fetchPolicy: 'network-only'
			});

			const vehicle = data?.vehicle;
			if (!vehicle) {
				toast.error(t('vehicles:messages.cloneError'));
				return;
			}

			// Map to form data and apply clone function for new IDs
			const formData = mapVehicleToFormData(vehicle);
			const clonedData = cloneVehicle({
				plateNumber: vehicle.plateNumber,
				make: vehicle.make,
				model: vehicle.model,
				color: vehicle.color,
				vehicleTypeId: vehicle.vehicleTypeId,
				customerId: vehicle.customerId,
				isActive: vehicle.isActive
			});

			go({
				to: '/vehicles/create',
				state: {
					cloneData: {
						...clonedData,
						customerDisplayName: formData.customerDisplayName
					}
				}
			});
		} catch (error) {
			console.error('Failed to clone vehicle:', error);
			toast.error(t('vehicles:messages.cloneError'));
		}
	};

	const columns: ColDef<VehicleListRecord>[] = useMemo(
		() => [
			{
				headerName: t('list.columns.plateNumber'),
				field: 'plateNumber',
				minWidth: 160,
				flex: 1,
				valueGetter: (params: ValueGetterParams<VehicleListRecord>) =>
					params.data?.plateNumber ?? 'Unknown',
				cellRenderer: (
					params: ValueGetterParams<VehicleListRecord>
				) => {
					const plateDisplay =
						params.data?.plateNumber ??
						params.data?.vehicleId ??
						'Unknown';
					const vehicleTypeLabel =
						params.data?.vehicleType?.name ??
						t('details.unassigned');
					return (
						<div className="flex flex-col py-1">
							<div className="font-medium">{plateDisplay}</div>
							<div className="text-xs text-muted-foreground">
								{vehicleTypeLabel}
							</div>
						</div>
					);
				}
			},
			{
				headerName: t('list.columns.makeModel'),
				colId: 'makeModel',
				minWidth: 180,
				flex: 1,
				valueGetter: (params: ValueGetterParams<VehicleListRecord>) =>
					params.data ? resolveMakeModel(params.data) : 'Unknown'
			},
			{
				headerName: t('list.columns.customer'),
				colId: 'customer',
				minWidth: 180,
				flex: 1,
				valueGetter: (params: ValueGetterParams<VehicleListRecord>) =>
					params.data ? resolveCustomerName(params.data) ?? '—' : '—'
			},
			{
				headerName: t('list.columns.status'),
				field: 'isActive',
				width: 120,
				minWidth: 120,
				cellRenderer: ActiveInactiveCellRenderer
			},
			{
				headerName: '',
				field: undefined,
				width: 60,
				minWidth: 60,
				maxWidth: 60,
				cellRenderer: DeleteCellRenderer,
				cellRendererParams: {
					onDelete: handleDeleteVehicle,
					disabled: deleteLoading
				},
				sortable: false,
				filter: false,
				resizable: false
			}
		],
		[t, handleDeleteVehicle, deleteLoading]
	);

	return (
		<>
			{error && (
				<div className="flex items-start p-4">
					<Alert variant="destructive" className="max-w-full">
						<AlertTitle>Unable to load vehicles</AlertTitle>
						<AlertDescription>
							{error.message ??
								'Failed to retrieve vehicle data.'}
						</AlertDescription>
					</Alert>
				</div>
			)}
			<ListPageLayout
				columns={columns}
				data={vehicles}
				title={t('list.title')}
				description={t('list.description')}
				searchPlaceholder={t('list.searchPlaceholder')}
				isLoading={loading || deleteLoading}
				selectedItem={selectedVehicle}
				detailsTitle="Vehicle Details"
				onRowClick={vehicle => {
					setSelectedVehicle(vehicle);
				}}
				onCreate={goToCreate}
				onEdit={vehicleId => goToEdit(vehicleId)}
				onDelete={handleDeleteVehicleById}
				onClone={handleCloneVehicle}
				onOpenInNewWindow={id => openEntityInNewWindow('vehicles', id)}
				onRefresh={handleRefetch}
				getRecordId={vehicle => vehicle.vehicleId}
				renderDetailsContent={vehicle => (
					<VehicleDetails
						vehicleId={vehicle.vehicleId}
						activeTab={activeDetailsTab}
						onTabChange={setActiveDetailsTab}
						hideTabsList={false}
					/>
				)}
				getDetailsDescription={vehicle =>
					vehicle.plateNumber ?? 'Unnamed vehicle'
				}
				getStatusBadge={getStatusBadge}
				renderEntityIcon={vehicle => (
					<VehicleEntityIcon
						vehicleTypeName={vehicle.vehicleType?.name}
						makeName={vehicle.make}
					/>
				)}
				getEntityTypeName={vehicle =>
					vehicle.vehicleType?.name ?? t('details.unassigned')
				}
				hasNextPage={hasNextPage}
				isLoadingMore={isLoadingMore}
				onLoadMore={handleLoadMore}
				entityName={t('entityName')}
			/>
		</>
	);
};
